// Every way this package refuses an input, with a stable code.
//
// The codes are part of the shared contract: a test vector can say `"error": { "code":
// "currency-mismatch" }` and both the TypeScript and the C# implementation must reject that input
// the same way (test-vectors/README.md). They are kebab-case like the API's error catalogue
// (spec/08 §2.6), but they are not problem types: nothing in this package speaks HTTP.
/** The complete list. Adding one means adding it here and saying so in test-vectors/README.md. */
export const DOMAIN_ERROR_CODES = [
    /** Two amounts in different currencies were added, compared or allocated together. */
    'currency-mismatch',
    /** An amount left the range the API guarantees (±10^13 minor units, spec/06 §1.1). */
    'money-out-of-range',
    /** A value that must be a whole number was not one — minor units and seconds are never floats. */
    'not-an-integer',
    /** A value outside the range its rule allows: a negative weight, a bad billing unit, bad bps. */
    'out-of-range',
    /** A ratio with a zero denominator. */
    'division-by-zero',
    /** An allocation with nothing to allocate across. */
    'empty-allocation',
    /** A timestamp that is not RFC 3339 in UTC, or a local date/time that is not well formed. */
    'invalid-moment',
    /** A timezone name no ICU database knows. */
    'invalid-timezone',
    /** A time window whose start, end or days do not form a window (spec/06 §3.1). */
    'invalid-time-window',
    /** A week of opening hours whose intervals overlap (spec/13 §13, F-ORG-01). */
    'invalid-opening-hours',
    /** A key, secret or code of the wrong shape (spec/05 §8.4). */
    'invalid-credential',
    /** A journal entry whose lines do not sum to zero in some unit (spec/06 §2.1). */
    'ledger-unbalanced',
];
/**
 * The one error this package throws. Callers switch on `code`, never on the message: the message
 * is for a developer reading a stack trace, the code is the contract.
 */
export class DomainError extends Error {
    name = 'DomainError';
    code;
    details;
    constructor(code, message, details = {}) {
        super(message);
        this.code = code;
        this.details = details;
    }
}
export function isDomainError(value) {
    return value instanceof DomainError;
}
/** Internal: the guard used wherever a caller could hand us a float where a whole number belongs. */
export function assertInteger(value, what) {
    if (!Number.isSafeInteger(value)) {
        throw new DomainError('not-an-integer', `${what} must be a whole number within ±2^53, got ${value}.`, {
            what,
            value,
        });
    }
}
/** Internal: a bounded whole number. `max` is inclusive; omit it for "no upper bound". */
export function assertIntegerInRange(value, what, min, max) {
    assertInteger(value, what);
    if (value < min || (max !== undefined && value > max)) {
        const bound = max === undefined ? `at least ${min}` : `between ${min} and ${max}`;
        throw new DomainError('out-of-range', `${what} must be ${bound}, got ${value}.`, { what, value, min, max });
    }
}
//# sourceMappingURL=errors.js.map